/**
 * Geometry helpers for ownership-graph edges (Bezier paths, label anchors,
 * lane offsets so parallel edges / labels do not stack on one line).
 */

import type { GraphEdge } from '../../../types'
import type { PositionedNode } from './layoutTree'

const CP_FRAC = 0.5

/** Horizontal shift of Bezier control points per lane (px). */
const LANE_STEP = 18

/** How far a shortcut edge bulges sideways to clear a skipped intermediate node. */
const BYPASS_BULGE = 80

/** Sample t along curve; spread when multiple edges share the same target. */
const T_CENTER = 0.5
const T_SPREAD = 0.06

/** Parametric t for ownership % badge — geometric midpoint of the cubic. */
const LABEL_T = 0.5

/** Approximate label box for collision (px). */
const LABEL_H = 15
function approxLabelW(text: string): number {
  return Math.max(34, Math.ceil(text.length * 5.8) + 8)
}

export interface CurvedEdgeGeom {
  path: string
  /** Label centre — on-curve at parametric midpoint (t = 0.5) */
  lx: number
  ly: number
  /** Unit normal at label point (legacy; callers may ignore) */
  nx: number
  ny: number
  /** Cubic Bezier control points — callers may sample the curve at arbitrary t */
  P0: { x: number; y: number }
  C1: { x: number; y: number }
  C2: { x: number; y: number }
  P3: { x: number; y: number }
}

/** Sample a cubic Bezier at parametric position t ∈ [0, 1]. */
export function pointOnCubic(t: number, e: CurvedEdgeGeom): { x: number; y: number } {
  const mt = 1 - t
  const a = mt * mt * mt
  const b = 3 * mt * mt * t
  const c = 3 * mt * t * t
  const d = t * t * t
  return {
    x: a * e.P0.x + b * e.C1.x + c * e.C2.x + d * e.P3.x,
    y: a * e.P0.y + b * e.C1.y + c * e.C2.y + d * e.P3.y,
  }
}

function bezierDeriv(
  t: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
): { x: number; y: number; dx: number; dy: number } {
  const mt = 1 - t
  const mt2 = mt * mt
  const t2 = t * t
  const a = mt2 * mt
  const b = 3 * mt2 * t
  const c = 3 * mt * t2
  const d = t * t2
  const x = a * x0 + b * x1 + c * x2 + d * x3
  const y = a * y0 + b * y1 + c * y2 + d * y3
  const dx = 3 * mt2 * (x1 - x0) + 6 * mt * t * (x2 - x1) + 3 * t2 * (x3 - x2)
  const dy = 3 * mt2 * (y1 - y0) + 6 * mt * t * (y2 - y1) + 3 * t2 * (y3 - y2)
  return { x, y, dx, dy }
}

/** Walk owner → owned edges downward. */
function canReachOwned(edges: GraphEdge[], start: string, end: string): boolean {
  if (start === end) return true
  const stack = [start]
  const seen = new Set<string>([start])
  while (stack.length) {
    const n = stack.pop()!
    for (const e of edges) {
      if (e.from !== n) continue
      if (e.to === end) return true
      if (!seen.has(e.to)) {
        seen.add(e.to)
        stack.push(e.to)
      }
    }
  }
  return false
}

/**
 * Nodes on tiers strictly between `from` and `to` that lie on an ownership
 * chain between them (the edge visually "skips over" these boxes).
 */
export function intermediateNodesSkippedByEdge(
  fromId: string,
  toId: string,
  positions: Record<string, PositionedNode>,
  edges: GraphEdge[],
): PositionedNode[] {
  const from = positions[fromId]
  const to = positions[toId]
  if (!from || !to || from.level < to.level + 2) return []

  const mids: PositionedNode[] = []
  for (const p of Object.values(positions)) {
    if (p.id === fromId || p.id === toId) continue
    if (p.level <= to.level || p.level >= from.level) continue
    if (canReachOwned(edges, fromId, p.id) && canReachOwned(edges, p.id, toId)) {
      mids.push(p)
    }
  }
  return mids.sort((a, b) => a.id.localeCompare(b.id))
}

/** +1 = route to the right of the column, −1 = to the left. */
export function detourSignForBypass(
  from: PositionedNode,
  to: PositionedNode,
  intermediates: PositionedNode[],
): number {
  if (intermediates.length === 0) return 1
  const columnCx = (nodeCenterX(from) + nodeCenterX(to)) / 2
  let left = 0
  let right = 0
  for (const m of intermediates) {
    const mc = nodeCenterX(m)
    if (mc < columnCx - 4) left++
    else if (mc > columnCx + 4) right++
  }
  if (left > right) return 1
  if (right > left) return -1
  return 1
}

function nodeCenterX(p: PositionedNode): number {
  return p.x + p.w / 2
}

/**
 * Cubic Bezier from bottom-centre of owner `from` to top-centre of owned `to`,
 * with horizontal bias on control points so parallel edges fan out.
 *
 * Layout guarantees `from.level > to.level`, so every edge is strictly
 * vertical (owner above owned). Shortcut edges (2+ tier gap) with
 * intermediate nodes bulge sideways so the stroke passes **around** those
 * boxes instead of behind them.
 */
export function buildCurvedOwnerEdge(
  from: PositionedNode,
  to: PositionedNode,
  laneOffsetX: number,
  intermediates: PositionedNode[] = [],
): CurvedEdgeGeom {
  const sx = from.x + from.w / 2
  const sy = from.y + from.h
  const ex = to.x + to.w / 2
  const ey = to.y
  const spanY = Math.abs(ey - sy)
  const cp = spanY * CP_FRAC

  const levelGap = from.level - to.level
  const bypass = levelGap >= 2 && intermediates.length > 0

  let c1x: number
  let c1y: number
  let c2x: number
  let c2y: number

  if (bypass) {
    const sign = detourSignForBypass(from, to, intermediates)
    const bulge = sign * (BYPASS_BULGE + Math.abs(laneOffsetX) * 0.5)
    const outX = (sx + ex) / 2 + bulge + laneOffsetX
    c1x = outX
    c1y = sy + cp * 0.5
    c2x = outX
    c2y = ey - cp * 0.5
  } else {
    c1x = sx + laneOffsetX
    c1y = sy + cp
    c2x = ex + laneOffsetX
    c2y = ey - cp
  }

  const path = `M ${sx},${sy} C ${c1x},${c1y} ${c2x},${c2y} ${ex},${ey}`

  const tLabel = Math.min(0.92, Math.max(0.08, LABEL_T))
  const { x, y, dx, dy } = bezierDeriv(tLabel, sx, sy, c1x, c1y, c2x, c2y, ex, ey)
  const len = Math.hypot(dx, dy) || 1
  let nx = -dy / len
  let ny = dx / len
  if (ny > 0) {
    nx = -nx
    ny = -ny
  }

  // Badge centre exactly on the stroke (parametric midpoint of the curve).
  const lx = x
  const ly = y

  return {
    path, lx, ly, nx, ny,
    P0: { x: sx, y: sy },
    C1: { x: c1x, y: c1y },
    C2: { x: c2x, y: c2y },
    P3: { x: ex, y: ey },
  }
}

/**
 * For each edge index, horizontal control-point offset so edges that share
 * the same child (`to`) or the same owner (`from`) fan out instead of stacking.
 */
export function computeLaneOffsetXPerEdge(
  edges: GraphEdge[],
): { offsetX: number; tSample: number }[] {
  const n = edges.length
  const offsetX: number[] = new Array(n).fill(0)
  const tSample: number[] = new Array(n).fill(T_CENTER)

  const byTo = new Map<string, number[]>()
  edges.forEach((e, i) => {
    const arr = byTo.get(e.to) ?? []
    arr.push(i)
    byTo.set(e.to, arr)
  })

  for (const indices of byTo.values()) {
    const sorted = [...indices].sort((a, b) => edges[a].from.localeCompare(edges[b].from))
    const mid = (sorted.length - 1) / 2
    sorted.forEach((edgeIdx, k) => {
      offsetX[edgeIdx] += (k - mid) * LANE_STEP
      tSample[edgeIdx] = T_CENTER + (k - mid) * T_SPREAD
    })
  }

  const byFrom = new Map<string, number[]>()
  edges.forEach((e, i) => {
    const arr = byFrom.get(e.from) ?? []
    arr.push(i)
    byFrom.set(e.from, arr)
  })

  for (const indices of byFrom.values()) {
    if (indices.length < 2) continue
    const sorted = [...indices].sort((a, b) => edges[a].to.localeCompare(edges[b].to))
    const mid = (sorted.length - 1) / 2
    sorted.forEach((edgeIdx, k) => {
      offsetX[edgeIdx] += (k - mid) * LANE_STEP
    })
  }

  return edges.map((_, i) => ({ offsetX: offsetX[i], tSample: tSample[i] }))
}

export interface LabelBox {
  cx: number
  cy: number
  w: number
  h: number
}

/**
 * Iteratively separate axis-aligned label boxes (small O(n²), n ≤ ~20).
 */
export function separateLabelBoxes(boxes: LabelBox[]): { dx: number; dy: number }[] {
  const n = boxes.length
  const nudge: { dx: number; dy: number }[] = Array.from({ length: n }, () => ({ dx: 0, dy: 0 }))
  const minGap = 4
  const iterations = 8

  for (let it = 0; it < iterations; it++) {
    let moved = false
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const bi = {
          left: boxes[i].cx + nudge[i].dx - boxes[i].w / 2,
          right: boxes[i].cx + nudge[i].dx + boxes[i].w / 2,
          top: boxes[i].cy + nudge[i].dy - boxes[i].h / 2,
          bottom: boxes[i].cy + nudge[i].dy + boxes[i].h / 2,
        }
        const bj = {
          left: boxes[j].cx + nudge[j].dx - boxes[j].w / 2,
          right: boxes[j].cx + nudge[j].dx + boxes[j].w / 2,
          top: boxes[j].cy + nudge[j].dy - boxes[j].h / 2,
          bottom: boxes[j].cy + nudge[j].dy + boxes[j].h / 2,
        }
        const ox = Math.min(bi.right, bj.right) - Math.max(bi.left, bj.left)
        const oy = Math.min(bi.bottom, bj.bottom) - Math.max(bi.top, bj.top)
        if (ox > 0 && oy > 0) {
          if (ox < oy) {
            const push = (ox + minGap) / 2
            nudge[i].dx -= push
            nudge[j].dx += push
          } else {
            const push = (oy + minGap) / 2
            nudge[i].dy -= push
            nudge[j].dy += push
          }
          moved = true
        }
      }
    }
    if (!moved) break
  }
  return nudge
}

export function labelBoxForText(text: string, cx: number, cy: number): LabelBox {
  return { cx, cy, w: approxLabelW(text), h: LABEL_H }
}

// ---------------------------------------------------------------------------
// Label deconfliction along the curves
// ---------------------------------------------------------------------------

export interface LabelPlacement {
  /** Parametric position along the cubic at which the label is centred */
  t:   number
  /** Effective label centre (= point on curve at `t`) */
  cx:  number
  cy:  number
  /** Bounding box dimensions */
  w:   number
  h:   number
}

interface DeconflictInput {
  /** Edge cubic; null = no curve / no label (skip) */
  edge: CurvedEdgeGeom | null
  /** Bounding box dimensions for the badge */
  w:    number
  h:    number
}

const T_DECONFLICT_MIN  = 0.18
const T_DECONFLICT_MAX  = 0.82
const T_DECONFLICT_STEP = 0.04
const T_DECONFLICT_GAP  = 4   // extra px between labels after deconflict
const T_DECONFLICT_ITER = 12

function rectsOverlap(
  ax: number, ay: number, aw: number, ah: number,
  bx: number, by: number, bw: number, bh: number,
  gap = 0,
): boolean {
  return (
    ax - aw / 2 - gap < bx + bw / 2 &&
    ax + aw / 2 + gap > bx - bw / 2 &&
    ay - ah / 2 - gap < by + bh / 2 &&
    ay + ah / 2 + gap > by - bh / 2
  )
}

/**
 * Slide each label along its own edge curve until labels stop overlapping
 * each other. The label centre always stays **on the stroke** — we only
 * change `t`. Overlapping pairs are nudged in opposite directions by the
 * same small step so neither label travels far from its midpoint.
 *
 * Inputs with `edge: null` (e.g. beneficial edges with no label) are
 * silently skipped; the returned array preserves index alignment.
 */
export function deconflictLabelsOnCurves(inputs: DeconflictInput[]): LabelPlacement[] {
  const ts: number[] = inputs.map(() => 0.5)
  const placements: LabelPlacement[] = inputs.map((inp, i) => {
    if (!inp.edge) {
      return { t: 0.5, cx: 0, cy: 0, w: inp.w, h: inp.h }
    }
    const p = pointOnCubic(ts[i], inp.edge)
    return { t: 0.5, cx: p.x, cy: p.y, w: inp.w, h: inp.h }
  })

  for (let iter = 0; iter < T_DECONFLICT_ITER; iter++) {
    let moved = false
    for (let i = 0; i < inputs.length; i++) {
      if (!inputs[i].edge) continue
      for (let j = i + 1; j < inputs.length; j++) {
        if (!inputs[j].edge) continue
        const a = placements[i]
        const b = placements[j]
        if (
          !rectsOverlap(a.cx, a.cy, a.w, a.h, b.cx, b.cy, b.w, b.h, T_DECONFLICT_GAP)
        ) continue

        // Both labels are over-lapping — slide each a small step in
        // opposite directions along its own curve.
        const aGoUp = a.cy <= b.cy  // the higher one slides toward t = 0 (toward owner)
        const aNewT = clamp(ts[i] + (aGoUp ? -T_DECONFLICT_STEP : T_DECONFLICT_STEP),
                            T_DECONFLICT_MIN, T_DECONFLICT_MAX)
        const bNewT = clamp(ts[j] + (aGoUp ? T_DECONFLICT_STEP : -T_DECONFLICT_STEP),
                            T_DECONFLICT_MIN, T_DECONFLICT_MAX)

        if (aNewT !== ts[i]) {
          ts[i] = aNewT
          const p = pointOnCubic(aNewT, inputs[i].edge!)
          placements[i] = { ...a, t: aNewT, cx: p.x, cy: p.y }
          moved = true
        }
        if (bNewT !== ts[j]) {
          ts[j] = bNewT
          const p = pointOnCubic(bNewT, inputs[j].edge!)
          placements[j] = { ...b, t: bNewT, cx: p.x, cy: p.y }
          moved = true
        }
      }
    }
    if (!moved) break
  }

  return placements
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}

/**
 * Hover highlight: **full ancestor cone + full descendant cone** through
 * `hoveredId`. Walks all owner chains upward to every topmost ancestor
 * (so we always reveal *every* ultimate owner, not just one) and all
 * downstream chains until the root. Beneficial edges are followed too —
 * if someone is a real owner only via a beneficial relationship, they
 * still light up.
 *
 * `rootId` is accepted but no longer needed for the traversal; it remains
 * in the signature so callers don't have to be changed.
 */
export function buildHighlightLineThrough(
  hoveredId: string,
  _rootId: string,
  edges: GraphEdge[],
): Set<string> {
  void _rootId
  const out = new Set<string>([hoveredId])

  // Walk UP — every owner transitively
  const upQ: string[] = [hoveredId]
  const upSeen = new Set<string>([hoveredId])
  while (upQ.length) {
    const cur = upQ.shift()!
    for (const e of edges) {
      if (e.to !== cur) continue
      if (!upSeen.has(e.from)) {
        upSeen.add(e.from)
        out.add(e.from)
        upQ.push(e.from)
      }
    }
  }

  // Walk DOWN — everything the hovered node owns transitively
  const downQ: string[] = [hoveredId]
  const downSeen = new Set<string>([hoveredId])
  while (downQ.length) {
    const cur = downQ.shift()!
    for (const e of edges) {
      if (e.from !== cur) continue
      if (!downSeen.has(e.to)) {
        downSeen.add(e.to)
        out.add(e.to)
        downQ.push(e.to)
      }
    }
  }

  return out
}
